import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  Plus, Search, Phone, MoreVertical, Pencil, Landmark,
  XCircle, ArrowRightCircle, History, Flame, Sparkles, Mail, Inbox,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from '../../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import API_BASE from '../../utils/apiBase';
import OutreachChannelBar from '../../components/portal/OutreachChannelBar';
import LeadEmailComposer from '../../components/portal/LeadEmailComposer';
import SalesOutreachCompliance from '../../components/portal/SalesOutreachCompliance';
import StaffingPipelineGuide from '../../components/portal/StaffingPipelineGuide';
import {
  LEAD_SOURCES,
  OUTREACH_CHANNELS,
  ACTIVITY_OUTCOMES,
  LEAD_TEMPERATURES,
  PREFERRED_CHANNELS,
  OUTREACH_PLAYBOOK,
  labelForSource,
  labelForChannel,
  labelForOutcome,
  suggestNextChannel,
  defaultActivitySummary,
  outcomeSuggestsFollowUpDays,
  addDaysIso,
  getLeadChannelCoverage,
} from '../../config/salesCrm';

const API = API_BASE;

const ORG_TYPES = [
  { value: 'care_home', label: 'Care Home' },
  { value: 'supported_living_provider', label: 'Supported Living' },
  { value: 'domiciliary_care_provider', label: 'Domiciliary Care' },
  { value: 'nhs_trust', label: 'NHS Trust' },
  { value: 'local_authority', label: 'Local Authority' },
  { value: 'private_provider', label: 'Private Provider' },
  { value: 'charity', label: 'Charity' },
  { value: 'other', label: 'Other' },
];

const PIPELINE_STAGES = [
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'interested', label: 'Interested' },
  { value: 'quoted', label: 'Quoted' },
  { value: 'won', label: 'Won (client)' },
  { value: 'lost', label: 'Lost' },
  { value: 'not_interested', label: 'Not interested' },
];

const STAGE_COLORS = {
  new: 'bg-blue-100 text-blue-700',
  contacted: 'bg-sky-100 text-sky-700',
  interested: 'bg-amber-100 text-amber-800',
  quoted: 'bg-purple-100 text-purple-700',
  won: 'bg-green-100 text-green-700',
  lost: 'bg-gray-100 text-gray-500',
  not_interested: 'bg-gray-100 text-gray-500',
};

const STAGE_LABELS = Object.fromEntries(PIPELINE_STAGES.map((s) => [s.value, s.label]));
const TEMP_COLORS = Object.fromEntries(LEAD_TEMPERATURES.map((t) => [t.value, t.color]));

const EMPTY_FORM = {
  company_name: '',
  organisation_type: 'care_home',
  contact_name: '',
  contact_email: '',
  contact_phone: '',
  postcode: '',
  job_title: '',
  source: 'cold_call',
  pipeline_stage: 'new',
  follow_up_at: '',
  social_handle: '',
  linkedin_url: '',
  facebook_url: '',
  website_url: '',
  cqc_location_id: '',
  address_line_1: '',
  preferred_channel: 'call',
  lead_temperature: 'cold',
  notes: '',
  do_not_contact: false,
};

function toDatetimeLocalValue(iso) {
  if (!iso) return '';
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
}

function fmtWhen(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function isOverdue(iso) {
  return iso && new Date(iso).getTime() <= Date.now();
}

function leadToEditForm(lead) {
  return {
    company_name: lead.company_name || '',
    organisation_type: lead.organisation_type || 'care_home',
    contact_name: lead.contact_name || '',
    contact_email: lead.contact_email || '',
    contact_phone: lead.contact_phone || '',
    postcode: lead.postcode || '',
    job_title: lead.job_title || '',
    source: lead.source || 'cold_call',
    pipeline_stage: lead.pipeline_stage || 'new',
    follow_up_at: toDatetimeLocalValue(lead.follow_up_at),
    social_handle: lead.social_handle || '',
    linkedin_url: lead.linkedin_url || '',
    facebook_url: lead.facebook_url || '',
    website_url: lead.website_url || '',
    cqc_location_id: lead.cqc_location_id || '',
    address_line_1: lead.address_line_1 || '',
    preferred_channel: lead.preferred_channel || 'call',
    lead_temperature: lead.lead_temperature || 'cold',
    notes: lead.notes || '',
    do_not_contact: Boolean(lead.do_not_contact),
  };
}

export default function SalesLeadsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [stageFilter, setStageFilter] = useState('all');
  const [followUpDueOnly, setFollowUpDueOnly] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [drawerLead, setDrawerLead] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [activityType, setActivityType] = useState('call');
  const [activityOutcome, setActivityOutcome] = useState('connected');
  const [activitySummary, setActivitySummary] = useState('');
  const [activitySaving, setActivitySaving] = useState(false);
  const [convertTarget, setConvertTarget] = useState(null);
  const [converting, setConverting] = useState(false);
  const [lostTarget, setLostTarget] = useState(null);
  const [lostReason, setLostReason] = useState('');

  useEffect(() => {
    if (searchParams.get('follow_up_due') === '1') setFollowUpDueOnly(true);
    if (searchParams.get('create') === '1') setShowCreate(true);
    const q = searchParams.get('search');
    if (q) setSearchInput(q);
  }, [searchParams]);

  useEffect(() => {
    const t = setTimeout(() => setSearchQuery(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const fetchLeads = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (stageFilter !== 'all') params.set('pipeline_stage', stageFilter);
      if (followUpDueOnly) params.set('follow_up_due', 'true');
      if (searchQuery) {
        params.set('search', searchQuery);
        params.set('include_closed', 'true');
      } else if (stageFilter === 'all' && !followUpDueOnly) {
        params.set('include_closed', 'false');
      }
      const qs = params.toString();
      const res = await fetch(`${API}/sales-leads${qs ? `?${qs}` : ''}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setLeads(data.sales_leads || []);
      } else {
        toast.error('Failed to load sales leads');
      }
    } catch {
      toast.error('Failed to load sales leads');
    } finally {
      setLoading(false);
    }
  }, [stageFilter, followUpDueOnly, searchQuery]);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(fetchLeads, 0);
    return () => clearTimeout(t);
  }, [fetchLeads]);

  useEffect(() => {
    const leadId = searchParams.get('lead');
    if (!leadId) return;
    const token = localStorage.getItem('token');
    fetch(`${API}/sales-leads/${leadId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data?.sales_lead) openDrawer(data.sales_lead);
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get('lead')]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.company_name.trim()) {
      toast.error('Care home / company name is required');
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/sales-leads`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          follow_up_at: form.follow_up_at ? new Date(form.follow_up_at).toISOString() : undefined,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.possible_duplicate) {
          toast.warning(`Lead created — possible duplicate: ${data.possible_duplicate.company_name}`);
        } else {
          toast.success(`Lead logged: ${data.sales_lead.lead_code}`);
        }
        setShowCreate(false);
        setForm(EMPTY_FORM);
        fetchLeads();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to create lead');
      }
    } catch {
      toast.error('Failed to create lead');
    } finally {
      setSaving(false);
    }
  };

  const openDrawer = (lead) => {
    setDrawerLead(lead);
    setEditForm(leadToEditForm(lead));
    const next = suggestNextChannel(lead);
    setActivityType(next.channel);
    setActivityOutcome('connected');
    setActivitySummary(defaultActivitySummary(next.channel, lead));
  };

  const selectChannelForLog = (channel) => {
    setActivityType(channel);
    if (drawerLead) {
      setActivitySummary(defaultActivitySummary(channel, drawerLead));
    }
    const outcomeDefaults = {
      call: 'connected',
      email: 'email_sent',
      sms: 'message_sent',
      whatsapp: 'message_sent',
      linkedin: 'message_sent',
      instagram: 'message_sent',
      facebook: 'message_sent',
      in_person: 'connected',
      video_call: 'connected',
      letter: 'message_sent',
      note: 'connected',
    };
    setActivityOutcome(outcomeDefaults[channel] || 'connected');
  };

  const saveDrawer = async () => {
    if (!drawerLead) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/sales-leads/${drawerLead.id}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...editForm,
          follow_up_at: editForm.follow_up_at ? new Date(editForm.follow_up_at).toISOString() : null,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setDrawerLead(data.sales_lead);
        toast.success('Lead updated');
        fetchLeads();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to update');
      }
    } catch {
      toast.error('Failed to update lead');
    } finally {
      setSaving(false);
    }
  };

  const logActivity = async () => {
    if (!drawerLead || !activitySummary.trim()) return;
    setActivitySaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/sales-leads/${drawerLead.id}/log-activity`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          activity_type: activityType,
          outcome: activityOutcome,
          summary: activitySummary.trim(),
          next_follow_up_at: addDaysIso(outcomeSuggestsFollowUpDays(activityOutcome)),
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setDrawerLead(data.sales_lead);
        setEditForm(leadToEditForm(data.sales_lead));
        const next = suggestNextChannel(data.sales_lead);
        setActivityType(next.channel);
        setActivitySummary(defaultActivitySummary(next.channel, data.sales_lead));
        toast.success('Touch logged — follow-up scheduled');
        fetchLeads();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to log activity');
      }
    } catch {
      toast.error('Failed to log activity');
    } finally {
      setActivitySaving(false);
    }
  };

  const convertToClient = async () => {
    if (!convertTarget) return;
    setConverting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/sales-leads/${convertTarget.id}/convert-to-organisation`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ mark_verified: true }),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`${convertTarget.company_name} is now a client — next: staffing request`, {
          description: 'Set charge rates (admin) on Client Organisations before invoicing.',
        });
        setConvertTarget(null);
        setDrawerLead(null);
        fetchLeads();
        if (data.organisation?.id) {
          navigate(`/portal/staffing-requests?org=${data.organisation.id}&create=1`);
        }
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to convert');
      }
    } catch {
      toast.error('Failed to convert lead');
    } finally {
      setConverting(false);
    }
  };

  const markLost = async () => {
    if (!lostTarget) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/sales-leads/${lostTarget.id}/mark-lost`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ pipeline_stage: 'lost', lost_reason: lostReason.trim() || undefined }),
      });
      if (res.ok) {
        toast.success('Lead closed');
        setLostTarget(null);
        setLostReason('');
        setDrawerLead(null);
        fetchLeads();
      }
    } catch {
      toast.error('Failed to close lead');
    }
  };

  const openCount = leads.filter((l) => !['won', 'lost', 'not_interested'].includes(l.pipeline_stage)).length;
  const dueCount = leads.filter((l) => isOverdue(l.follow_up_at) && !l.converted_organisation_id).length;
  const hotCount = leads.filter((l) => l.lead_temperature === 'hot' && !l.converted_organisation_id).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  const nextTouch = drawerLead ? suggestNextChannel(drawerLead) : null;
  const coverage = drawerLead ? getLeadChannelCoverage({ ...drawerLead, ...editForm }) : [];

  return (
    <div className="space-y-6" data-testid="sales-leads-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Sales CRM</h1>
          <p className="text-sm text-text-muted mt-1">
            Search any lead, then email them from Osabea — or use phone, WhatsApp, LinkedIn, and more.
          </p>
        </div>
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogTrigger asChild>
            <Button><Plus className="h-4 w-4 mr-2" />Log lead</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Log care home lead</DialogTitle>
              <DialogDescription>Add the home once — then use every channel from the lead record.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="space-y-2">
                <Label>Care home / company name *</Label>
                <Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} required />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Type</Label>
                  <select value={form.organisation_type} onChange={(e) => setForm({ ...form, organisation_type: e.target.value })} className="w-full px-3 py-2 rounded-lg border text-sm bg-white">
                    {ORG_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>How you found them</Label>
                  <select value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} className="w-full px-3 py-2 rounded-lg border text-sm bg-white">
                    {LEAD_SOURCES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Contact name</Label>
                  <Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} placeholder="Registered Manager" />
                </div>
                <div className="space-y-2">
                  <Label>Job title</Label>
                  <Input value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })} placeholder="Registered Manager" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Phone / mobile</Label>
                  <Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Instagram @</Label>
                  <Input value={form.social_handle} onChange={(e) => setForm({ ...form, social_handle: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>LinkedIn URL</Label>
                  <Input value={form.linkedin_url} onChange={(e) => setForm({ ...form, linkedin_url: e.target.value })} placeholder="linkedin.com/company/..." />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Facebook page URL</Label>
                  <Input value={form.facebook_url} onChange={(e) => setForm({ ...form, facebook_url: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Website URL</Label>
                  <Input value={form.website_url} onChange={(e) => setForm({ ...form, website_url: e.target.value })} placeholder="carehome.co.uk" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Postcode</Label>
                  <Input value={form.postcode} onChange={(e) => setForm({ ...form, postcode: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>CQC location ID</Label>
                  <Input value={form.cqc_location_id} onChange={(e) => setForm({ ...form, cqc_location_id: e.target.value })} placeholder="1-123456789" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Preferred channel</Label>
                  <select value={form.preferred_channel} onChange={(e) => setForm({ ...form, preferred_channel: e.target.value })} className="w-full px-3 py-2 rounded-lg border text-sm bg-white">
                    {PREFERRED_CHANNELS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Follow-up</Label>
                  <Input type="datetime-local" value={form.follow_up_at} onChange={(e) => setForm({ ...form, follow_up_at: e.target.value })} />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
              </div>
              <Button type="submit" disabled={saving} className="w-full">{saving ? 'Saving…' : 'Save lead'}</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <StaffingPipelineGuide currentStep={2} />

      <SalesOutreachCompliance compact />

      <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 text-sm">
        <p className="font-medium text-slate-900 flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" /> Multi-touch playbook (built in)</p>
        <p className="text-slate-600 mt-1">
          Top teams research first (CQC, Google), then rotate: {OUTREACH_PLAYBOOK.slice(0, 6).map((s) => labelForChannel(s.channel)).join(' → ')}…
          Open a lead, launch any channel, then <strong>Log touch</strong>.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="p-4 rounded-xl bg-white border"><p className="text-2xl font-bold">{openCount}</p><p className="text-xs text-text-muted">Open leads</p></div>
        <div className="p-4 rounded-xl bg-white border"><p className="text-2xl font-bold text-red-600">{dueCount}</p><p className="text-xs text-text-muted">Follow-up due</p></div>
        <div className="p-4 rounded-xl bg-white border"><p className="text-2xl font-bold text-orange-600">{hotCount}</p><p className="text-xs text-text-muted flex items-center gap-1"><Flame className="h-3 w-3" /> Hot leads</p></div>
        <div className="p-4 rounded-xl bg-white border"><p className="text-2xl font-bold">{leads.filter((l) => l.pipeline_stage === 'won').length}</p><p className="text-xs text-text-muted">Clients won</p></div>
      </div>

      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 max-w-lg">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-muted" />
          <Input
            placeholder="Search care home, contact, phone, email, postcode, lead code…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-10"
            data-testid="sales-leads-search"
          />
        </div>
        {searchQuery && (
          <p className="text-sm text-text-muted">{leads.length} result{leads.length === 1 ? '' : 's'}</p>
        )}
        <select value={stageFilter} onChange={(e) => setStageFilter(e.target.value)} className="px-3 py-2 rounded-lg border text-sm bg-white">
          <option value="all">All open stages</option>
          {PIPELINE_STAGES.filter((s) => !['won', 'lost', 'not_interested'].includes(s.value)).map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={followUpDueOnly} onChange={(e) => setFollowUpDueOnly(e.target.checked)} />
          Follow-up due
        </label>
      </div>

      {leads.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border">
          <Phone className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-text-muted mb-4">
            {searchQuery ? `No leads match "${searchQuery}"` : 'No leads yet — search the CQC register and save care homes in one click.'}
          </p>
          {!searchQuery && (
            <div className="flex flex-wrap gap-2 justify-center">
              <Button asChild><Link to="/portal/lead-discovery"><Search className="h-4 w-4 mr-2" />Find care homes</Link></Button>
              <Button variant="outline" onClick={() => setShowCreate(true)}><Plus className="h-4 w-4 mr-2" />Log manually</Button>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl border overflow-x-auto">
          <table className="w-full min-w-[800px]">
            <thead>
              <tr className="bg-gray-50 border-b text-xs uppercase text-text-muted">
                <th className="text-left px-4 py-3">Lead</th>
                <th className="text-left px-4 py-3">Heat</th>
                <th className="text-left px-4 py-3">Stage</th>
                <th className="text-left px-4 py-3">Touches</th>
                <th className="text-left px-4 py-3">Last touch</th>
                <th className="text-left px-4 py-3">Follow-up</th>
                <th className="text-left px-4 py-3">Contact</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y">
              {leads.map((lead) => (
                <tr key={lead.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => openDrawer(lead)}>
                  <td className="px-4 py-3">
                    <p className="font-medium text-sm">{lead.company_name}</p>
                    <p className="text-xs text-text-muted">{lead.lead_code} · {labelForSource(lead.source)}</p>
                    {(lead.contact_name || lead.contact_phone || lead.contact_email) && (
                      <p className="text-xs text-text-muted mt-0.5 truncate max-w-[220px]">
                        {[lead.contact_name, lead.contact_phone, lead.contact_email].filter(Boolean).join(' · ')}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${TEMP_COLORS[lead.lead_temperature] || TEMP_COLORS.cold}`}>
                      {lead.lead_temperature || 'cold'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${STAGE_COLORS[lead.pipeline_stage] || STAGE_COLORS.new}`}>
                      {STAGE_LABELS[lead.pipeline_stage] || lead.pipeline_stage}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-center">{lead.contact_attempts ?? 0}</td>
                  <td className="px-4 py-3 text-xs">
                    {lead.last_activity_type ? (
                      <>
                        <span className="font-medium">{labelForChannel(lead.last_activity_type)}</span>
                        {lead.last_activity_outcome && (
                          <span className="text-text-muted block">{labelForOutcome(lead.last_activity_outcome)}</span>
                        )}
                      </>
                    ) : '—'}
                  </td>
                  <td className={`px-4 py-3 text-sm ${isOverdue(lead.follow_up_at) ? 'text-red-600 font-medium' : 'text-text-muted'}`}>
                    {fmtWhen(lead.follow_up_at)}
                  </td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    {lead.contact_email ? (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs"
                        onClick={() => openDrawer(lead)}
                        title="Send email from Osabea"
                      >
                        <Mail className="h-3 w-3 mr-1" />
                        Email
                      </Button>
                    ) : lead.contact_phone ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 text-xs"
                        onClick={() => openDrawer(lead)}
                        title="Log a call"
                      >
                        <Phone className="h-3 w-3 mr-1" />
                        Call
                      </Button>
                    ) : (
                      <span className="text-xs text-text-muted">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0"><MoreVertical className="h-4 w-4" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openDrawer(lead)}><Pencil className="h-4 w-4 mr-2" />Open workspace</DropdownMenuItem>
                        {!lead.converted_organisation_id && (
                          <>
                            <DropdownMenuItem onClick={() => setConvertTarget(lead)}><Landmark className="h-4 w-4 mr-2" />Convert to client</DropdownMenuItem>
                            <DropdownMenuItem className="text-red-600" onClick={() => setLostTarget(lead)}><XCircle className="h-4 w-4 mr-2" />Mark lost</DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!drawerLead} onOpenChange={(o) => !o && setDrawerLead(null)}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          {drawerLead && (
            <>
              <DialogHeader>
                <DialogTitle>{drawerLead.company_name}</DialogTitle>
                <DialogDescription>
                  {drawerLead.lead_code} · {drawerLead.contact_attempts ?? 0} touches
                  {nextTouch && ` · Next suggested: ${labelForChannel(nextTouch.channel)}`}
                </DialogDescription>
              </DialogHeader>

              {editForm.do_not_contact && (
                <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
                  This lead opted out of contact. Do not call or email. You may mark as lost.
                </p>
              )}

              {!drawerLead.converted_organisation_id && !editForm.do_not_contact && (
                <LeadEmailComposer
                  lead={{ ...drawerLead, ...editForm }}
                  onSent={(updated) => {
                    setDrawerLead(updated);
                    setEditForm(leadToEditForm(updated));
                    fetchLeads();
                  }}
                />
              )}

              {editForm.contact_email && (
                <Button variant="outline" size="sm" asChild className="w-full">
                  <Link to={`/portal/inbox?department=staffing&contact=${encodeURIComponent(editForm.contact_email)}`}>
                    <Inbox className="h-4 w-4 mr-2" />
                    Open staffing email thread
                  </Link>
                </Button>
              )}

              {!drawerLead.converted_organisation_id && !editForm.do_not_contact && (
                <div className="space-y-2">
                  <div className="flex flex-wrap gap-1.5">
                    {coverage.map((c) => (
                      <span
                        key={c.key}
                        className={`text-[10px] px-2 py-0.5 rounded-full border ${c.ready ? 'bg-green-50 border-green-200 text-green-800' : 'bg-gray-50 border-gray-200 text-gray-500'}`}
                      >
                        {c.label}{c.ready ? ' ✓' : ' —'}
                      </span>
                    ))}
                  </div>
                  <Label className="text-xs font-semibold uppercase tracking-wide text-text-muted">Other channels (opens your phone / apps)</Label>
                  <OutreachChannelBar lead={{ ...drawerLead, ...editForm }} onSelectChannel={selectChannelForLog} />
                </div>
              )}

              {nextTouch && !drawerLead.converted_organisation_id && (
                <p className="text-xs bg-primary/5 border border-primary/10 rounded-lg p-2 text-primary">
                  Playbook step {nextTouch.step}: {nextTouch.hint}
                </p>
              )}

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-text-muted text-xs">Phone</span>
                  <p>{editForm.contact_phone || '—'}</p>
                </div>
                <div>
                  <span className="text-text-muted text-xs">Email</span>
                  <p className="truncate">{editForm.contact_email || '—'}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Pipeline</Label>
                  <select value={editForm.pipeline_stage} onChange={(e) => setEditForm({ ...editForm, pipeline_stage: e.target.value })} className="w-full px-3 py-2 rounded-lg border text-sm bg-white" disabled={!!drawerLead.converted_organisation_id}>
                    {PIPELINE_STAGES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Follow-up</Label>
                  <Input type="datetime-local" value={editForm.follow_up_at} onChange={(e) => setEditForm({ ...editForm, follow_up_at: e.target.value })} />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">LinkedIn</Label>
                  <Input value={editForm.linkedin_url} onChange={(e) => setEditForm({ ...editForm, linkedin_url: e.target.value })} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Instagram @</Label>
                  <Input value={editForm.social_handle} onChange={(e) => setEditForm({ ...editForm, social_handle: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Facebook</Label>
                  <Input value={editForm.facebook_url} onChange={(e) => setEditForm({ ...editForm, facebook_url: e.target.value })} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Website</Label>
                  <Input value={editForm.website_url} onChange={(e) => setEditForm({ ...editForm, website_url: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Postcode</Label>
                  <Input value={editForm.postcode} onChange={(e) => setEditForm({ ...editForm, postcode: e.target.value })} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">CQC location ID</Label>
                  <Input value={editForm.cqc_location_id} onChange={(e) => setEditForm({ ...editForm, cqc_location_id: e.target.value })} placeholder="From CQC profile URL" />
                </div>
              </div>

              <div className="space-y-1">
                <Label className="text-xs">Notes</Label>
                <Textarea value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} rows={2} />
              </div>
              <label className="flex items-center gap-2 text-sm text-red-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editForm.do_not_contact}
                  onChange={(e) => setEditForm({ ...editForm, do_not_contact: e.target.checked })}
                />
                Do not contact (legal opt-out — blocks emails)
              </label>
              <Button onClick={saveDrawer} disabled={saving} size="sm" variant="outline">Save details</Button>

              {!drawerLead.converted_organisation_id && (
                <div className="border-t pt-4 space-y-3">
                  <p className="text-sm font-medium flex items-center gap-2"><History className="h-4 w-4" />Log this touch</p>
                  <div className="grid grid-cols-2 gap-2">
                    <select value={activityType} onChange={(e) => selectChannelForLog(e.target.value)} className="px-3 py-2 rounded-lg border text-sm bg-white">
                      {OUTREACH_CHANNELS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                    <select value={activityOutcome} onChange={(e) => setActivityOutcome(e.target.value)} className="px-3 py-2 rounded-lg border text-sm bg-white">
                      {ACTIVITY_OUTCOMES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                  <Textarea value={activitySummary} onChange={(e) => setActivitySummary(e.target.value)} rows={3} />
                  <Button onClick={logActivity} disabled={activitySaving || !activitySummary.trim()} className="w-full">
                    {activitySaving ? 'Saving…' : 'Log touch & schedule follow-up'}
                  </Button>
                </div>
              )}

              {(drawerLead.activities || []).length > 0 && (
                <div className="border-t pt-4">
                  <p className="text-sm font-medium mb-2">Timeline</p>
                  <ul className="space-y-2 max-h-48 overflow-y-auto">
                    {[...(drawerLead.activities || [])].reverse().map((a) => (
                      <li key={a.id} className="text-xs bg-gray-50 rounded-lg p-3 border border-gray-100">
                        <div className="flex justify-between gap-2">
                          <span className="font-semibold">{labelForChannel(a.activity_type)}</span>
                          <span className="text-text-muted">{fmtWhen(a.created_at)}</span>
                        </div>
                        {a.outcome && <p className="text-text-muted mt-0.5">{labelForOutcome(a.outcome)}</p>}
                        {a.sent_from_portal && <p className="text-[10px] text-primary font-medium mt-0.5">Sent from Osabea portal</p>}
                        <p className="mt-1 text-text-primary">{a.summary}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {!drawerLead.converted_organisation_id ? (
                <Button className="w-full" onClick={() => setConvertTarget(drawerLead)}>
                  <Landmark className="h-4 w-4 mr-2" />Convert to client organisation
                </Button>
              ) : (
                <p className="text-sm text-green-700 bg-green-50 rounded-lg p-3">
                  Client organisation created. Manage under{' '}
                  <Link to="/portal/organisations" className="underline font-medium">Client Organisations</Link>.
                </p>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!convertTarget} onOpenChange={(o) => !o && setConvertTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Convert to client?</DialogTitle>
            <DialogDescription>
              Only when {convertTarget?.company_name} has agreed to use Osabea staffing services.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setConvertTarget(null)}>Cancel</Button>
            <Button onClick={convertToClient} disabled={converting}>{converting ? 'Converting…' : 'Create client'}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!lostTarget} onOpenChange={(o) => !o && setLostTarget(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Close lead</DialogTitle></DialogHeader>
          <Textarea value={lostReason} onChange={(e) => setLostReason(e.target.value)} placeholder="Reason (optional)" rows={2} />
          <Button variant="destructive" onClick={markLost}>Mark as lost</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}